import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Gift, Calculator } from "lucide-react";

import { specialOfferApi } from "@/lib/extensions-api";
import { formatCurrency } from "@/lib/format";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";

interface Form {
  user_id: string;
  total_reward: string;
  duration_days: number;
  note: string;
}

export function CreateSpecialOfferModal({
  open, onClose, prefilledUserId,
}: {
  open: boolean;
  onClose: () => void;
  prefilledUserId?: string;
}) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<Form>({
    defaultValues: { duration_days: 30 },
  });

  useEffect(() => {
    if (open && prefilledUserId) reset({ user_id: prefilledUserId, duration_days: 30 });
  }, [open, prefilledUserId, reset]);

  const total = Number(watch("total_reward") || 0);
  const days = Number(watch("duration_days") || 0);
  const daily = days > 0 ? total / days : 0;

  const create = useMutation({
    mutationFn: (v: Form) => specialOfferApi.create({
      user_id: v.user_id,
      total_reward: v.total_reward,
      duration_days: Number(v.duration_days),
      note: v.note,
    }),
    onSuccess: (o) => {
      toast.success(`Special Offer #${o.id} created — payouts start today.`);
      qc.invalidateQueries({ queryKey: ["admin-special-offers"] });
      reset();
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Special Offer" size="lg">
      <form onSubmit={handleSubmit((v) => create.mutate(v))} className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 text-xs text-brand-800 flex gap-2">
          <Gift className="h-4 w-4 shrink-0 mt-0.5" />
          The reward will be split evenly across the duration and credited to
          the target user's wallet daily via automated payout.
        </div>

        <Input
          label="Target User ID"
          placeholder="USR000123"
          disabled={!!prefilledUserId}
          error={errors.user_id?.message}
          {...register("user_id", { required: "User ID required" })}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Total Reward"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="3000.00"
            error={errors.total_reward?.message}
            {...register("total_reward", { required: "Total reward required" })}
          />
          <Input
            label="Duration (days)"
            type="number"
            min="1"
            max="365"
            {...register("duration_days", { required: true, valueAsNumber: true, min: 1 })}
          />
        </div>

        {total > 0 && days > 0 && (
          <div className="rounded-lg bg-gradient-to-br from-warning-50 to-warning-100 border border-warning-200 p-4">
            <div className="flex items-center gap-2 mb-2 text-warning-700">
              <Calculator className="h-4 w-4" />
              <div className="text-2xs font-bold uppercase tracking-wide">Payout Schedule Preview</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xs text-warning-700">Daily Payout</div>
                <div className="text-lg font-bold text-warning-900">
                  {formatCurrency(daily.toFixed(2))}
                </div>
              </div>
              <div>
                <div className="text-2xs text-warning-700">× Days</div>
                <div className="text-lg font-bold text-warning-900">{days}</div>
              </div>
              <div>
                <div className="text-2xs text-warning-700">= Total</div>
                <div className="text-lg font-bold text-warning-900">
                  {formatCurrency(total)}
                </div>
              </div>
            </div>
          </div>
        )}

        <Input label="Note (optional)" placeholder="Reason or reference"
                {...register("note")} />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={create.isPending}
                   leftIcon={<Gift className="h-4 w-4" />}>
            Create Offer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
